import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './components/SEO';
import { ThemeContext } from './components/Core/ThemeContext';
import './entity-directory.css';

const PAGE_SIZE = 120;

const directoryConfig = {
    people: {
        eyebrow: 'Biblical people and groups',
        title: 'People',
        description: 'Browse people and people groups named in the Bible, then open a profile to explore relationships, references, and historical notes.',
        indexUrl: '/data/people-index.json',
        detailPath: '/person/',
        itemLabel: 'person',
        placeholder: 'Search people by name',
        seoTitle: 'Bible People | ai-BIBLE',
    },
    places: {
        eyebrow: 'Biblical geography',
        title: 'Places',
        description: 'Browse places named in the Bible, then open a profile to explore references, geography, and historical notes.',
        indexUrl: '/data/places-index.json',
        detailPath: '/location/',
        itemLabel: 'place',
        placeholder: 'Search places by name',
        seoTitle: 'Bible Places | ai-BIBLE',
    },
};

const normalize = (value = '') => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

function PageEntityDirectory({ type }) {
    const theme = useContext(ThemeContext);
    const config = directoryConfig[type];
    const [entities, setEntities] = useState([]);
    const [query, setQuery] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        let active = true;
        window.scrollTo(0, 0);
        theme.loading(true);
        fetch(config.indexUrl)
            .then((response) => {
                if (!response.ok) throw new Error(`Could not load ${config.title.toLowerCase()}`);
                return response.json();
            })
            .then((entries) => {
                if (active) setEntities(entries);
            })
            .catch(() => {
                if (active) setLoadFailed(true);
            })
            .finally(() => {
                if (active) theme.loading(false);
            });
        return () => { active = false; };
    }, [config, theme]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [query]);

    const filteredEntities = useMemo(() => {
        const terms = normalize(query).split(' ').filter(Boolean);
        if (!terms.length) return entities;
        return entities.filter((entity) => {
            const name = normalize(entity.name);
            return terms.every((term) => name.includes(term));
        });
    }, [entities, query]);

    const visibleEntities = filteredEntities.slice(0, visibleCount);
    const pluralLabel = `${config.itemLabel}${filteredEntities.length === 1 ? '' : 's'}`;

    return (
        <main className={`entity-directory-page entity-directory-${type}`}>
            <SEO canonical={`/${type}`} title={config.seoTitle} description={config.description} />
            <div className="entity-directory-frame">
                <header className="entity-directory-hero">
                    <span className="entity-directory-hero-strip" aria-hidden="true" />
                    <div>
                        <p className="entity-directory-eyebrow">{config.eyebrow}</p>
                        <h1>{config.title}</h1>
                        <p>{config.description}</p>
                    </div>
                </header>

                <div className="entity-directory-search">
                    <label htmlFor={`${type}-search-input`}>Search {config.title.toLowerCase()}</label>
                    <div className="entity-directory-search-field">
                        <span aria-hidden="true">⌕</span>
                        <input
                            id={`${type}-search-input`}
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={config.placeholder}
                        />
                        {query ? <button type="button" onClick={() => setQuery('')}>Clear</button> : null}
                    </div>
                </div>

                <section aria-label={`${config.title} directory`}>
                    <div className="entity-directory-results">
                        <span>{filteredEntities.length.toLocaleString()} {pluralLabel}</span>
                        {visibleEntities.length < filteredEntities.length ? (
                            <span>Showing first {visibleEntities.length.toLocaleString()}</span>
                        ) : null}
                    </div>

                    {loadFailed ? (
                        <div className="entity-directory-empty">
                            <h2>Could not load {config.title.toLowerCase()}</h2>
                            <p>Please refresh the page and try again.</p>
                        </div>
                    ) : null}

                    {!loadFailed && entities.length > 0 && filteredEntities.length === 0 ? (
                        <div className="entity-directory-empty">
                            <h2>No matching {config.title.toLowerCase()}</h2>
                            <p>Try a shorter name or check the spelling.</p>
                            <button type="button" onClick={() => setQuery('')}>Clear search</button>
                        </div>
                    ) : null}

                    <div className="entity-directory-grid">
                        {visibleEntities.map((entity) => (
                            <Link className="entity-directory-card" to={`${config.detailPath}${entity.ref}`} key={entity.ref}>
                                <strong>{entity.name}</strong>
                                {entity.meaning ? <span>{entity.meaning}</span> : <span>Open profile</span>}
                                <small>{entity.verseCount} biblical reference{entity.verseCount === 1 ? '' : 's'}</small>
                            </Link>
                        ))}
                    </div>

                    {visibleEntities.length < filteredEntities.length ? (
                        <button
                            className="entity-directory-more"
                            type="button"
                            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                        >
                            Show more {config.title.toLowerCase()}
                        </button>
                    ) : null}
                </section>
            </div>
        </main>
    );
}

export default PageEntityDirectory;
